import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { 
  cognitoService, 
  type LoginCredentials, 
  type SignUpData, 
  type ConfirmSignUpData,
  type ForgotPasswordData,
  type ResetPasswordData,
  type CognitoUser,
  type AuthTokens
} from '../../services/auth/cognitoService'

export interface User {
  id: string
  username: string
  email: string
  firstName: string
  lastName: string
  name: string // Full name computed from firstName + lastName
  picture?: string // Profile picture URL - from federated provider or uploaded
  role: 'admin' | 'faculty' | 'student' | 'guest'
  faculty?: string // Faculty assignment - configurable by admin
  permissions: string[]
  preferences: {
    theme: 'light' | 'dark' | 'system'
    language: string
    notifications: boolean
  }
  emailVerified: boolean
  phoneVerified?: boolean
  createdAt?: string
  lastLoginAt?: string
}

export interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  loginAttempts: number
  lastLoginTime: string | null
  pendingVerification: {
    username: string
    email: string
  } | null
}

// Helper function to load auth state from localStorage
const loadAuthFromStorage = (): Partial<AuthState> => {
  try {
    const storedAuth = localStorage.getItem('auth_state')
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth)
      
      // Only load auth state if it has Cognito tokens (real authentication)
      // This prevents loading old mock authentication data
      if (parsed.user && parsed.isAuthenticated && parsed.tokens?.accessToken) {
        return {
          user: parsed.user,
          tokens: parsed.tokens,
          isAuthenticated: true,
          lastLoginTime: parsed.lastLoginTime
        }
      } else {
        // Clear invalid/mock auth data
        console.info('Clearing invalid auth state from localStorage')
        localStorage.removeItem('auth_state')
      }
    }
  } catch (error) {
    console.warn('Failed to load auth state from localStorage:', error)
    localStorage.removeItem('auth_state')
  }
  return {}
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginAttempts: 0,
  lastLoginTime: null,
  pendingVerification: null,
  ...loadAuthFromStorage()
}

// Helper function to map Cognito user to application user
function mapCognitoUserToUser(cognitoUser: CognitoUser): User {
  const firstName = cognitoUser.firstName || ''
  const lastName = cognitoUser.lastName || ''
  
  // Enhanced name fallback logic
  let name = ''
  if (firstName && lastName) {
    name = `${firstName} ${lastName}`.trim()
  } else if (firstName) {
    name = firstName
  } else if (lastName) {
    name = lastName
  } else if (cognitoUser.email) {
    // Extract name from email (better than showing user ID)
    name = cognitoUser.email.split('@')[0].replace(/[._-]/g, ' ')
  } else {
    name = cognitoUser.username || 'User'
  }
  
  // Determine user role with automatic admin assignment for first user
  let userRole: User['role'] = 'faculty' // Default role
  let permissions: string[] = []
  
  // Check if this is the first user (admin auto-assignment)
  try {
    const firstAdminSet = localStorage.getItem('ceu_first_admin_set')
    if (!firstAdminSet) {
      // This is the first user - make them admin
      userRole = 'admin'
      permissions = [
        'manage_users',
        'manage_roles',
        'manage_faculties',
        'manage_settings',
        'view_all_programs',
        'edit_all_programs',
        'delete_programs',
        'manage_uploads',
        'view_analytics',
        'manage_agents'
      ]
      localStorage.setItem('ceu_first_admin_set', 'true')
      localStorage.setItem('ceu_first_admin_id', cognitoUser.userId)
      console.log('🔥 First user detected - assigning admin role:', cognitoUser.email)
    } else {
      // Check role based on Cognito user attributes or groups
      // For now, use default faculty role with basic permissions
      userRole = 'faculty'
      permissions = [
        'view_programs',
        'create_programs',
        'edit_own_programs',
        'upload_documents',
        'view_analysis',
        'chat_access'
      ]
    }
  } catch (error) {
    console.warn('Error determining user role:', error)
    // Fallback to faculty role
    userRole = 'faculty'
    permissions = ['view_programs', 'create_programs', 'edit_own_programs']
  }
  
  // Preserve local profile picture if it exists, otherwise use Cognito's picture
  let profilePicture = cognitoUser.picture
  try {
    const storedAuth = localStorage.getItem('auth_state')
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth)
      if (parsed.user?.id === cognitoUser.userId && parsed.user?.picture && parsed.user.picture.startsWith('cloud-storage://')) {
        // Preserve the local uploaded profile picture
        profilePicture = parsed.user.picture
        console.log('🖼️ mapCognitoUserToUser: Preserving local profile picture:', profilePicture)
      }
    }
  } catch (error) {
    console.warn('Error checking for local profile picture:', error)
  }
  
  console.log('mapCognitoUserToUser:', {
    input: cognitoUser,
    output: { firstName, lastName, name, role: userRole, permissions, preservedPicture: profilePicture }
  })
  
  return {
    id: cognitoUser.userId,
    username: cognitoUser.username,
    email: cognitoUser.email,
    firstName: firstName,
    lastName: lastName,
    name: name,
    picture: profilePicture,
    role: userRole,
    permissions: permissions,
    preferences: {
      theme: 'system',
      language: 'en',
      notifications: true
    },
    emailVerified: cognitoUser.emailVerified,
    phoneVerified: cognitoUser.phoneVerified
  }
}

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const result = await cognitoService.signIn(credentials)
      return {
        user: mapCognitoUserToUser(result.user),
        tokens: result.tokens
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed')
    }
  }
)

export const signUpUser = createAsyncThunk(
  'auth/signUpUser',
  async (userData: SignUpData, { rejectWithValue }) => {
    try {
      const result = await cognitoService.signUp(userData)
      return {
        userId: result.userId,
        username: userData.username,
        email: userData.email,
        codeDeliveryDetails: result.codeDeliveryDetails
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Sign up failed')
    }
  }
)

export const confirmSignUp = createAsyncThunk(
  'auth/confirmSignUp',
  async (data: ConfirmSignUpData, { rejectWithValue }) => {
    try {
      await cognitoService.confirmSignUp(data)
      return { username: data.username }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Email confirmation failed')
    }
  }
)

export const resendConfirmationCode = createAsyncThunk(
  'auth/resendConfirmationCode',
  async (username: string, { rejectWithValue }) => {
    try {
      const codeDeliveryDetails = await cognitoService.resendConfirmationCode(username)
      return { username, codeDeliveryDetails }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to resend confirmation code')
    }
  }
)

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (data: ForgotPasswordData, { rejectWithValue }) => {
    try {
      const codeDeliveryDetails = await cognitoService.forgotPassword(data)
      return { username: data.username, codeDeliveryDetails }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to initiate password reset')
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: ResetPasswordData, { rejectWithValue }) => {
    try {
      await cognitoService.confirmResetPassword(data)
      return { username: data.username }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Password reset failed')
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const tokens = await cognitoService.refreshAuthSession()
      return tokens
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token refresh failed')
    }
  }
)

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      const result = await cognitoService.getCurrentAuthenticatedUser()
      if (result) {
        return {
          user: mapCognitoUserToUser(result.user),
          tokens: result.tokens
        }
      }
      return null
    } catch (error: any) {
      return rejectWithValue(error.message || 'Auth check failed')
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await cognitoService.signOut()
      return undefined
    } catch (error: any) {
      // Still proceed with logout even if Cognito signOut fails
      console.error('Cognito sign out error:', error)
      return undefined
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
    },
    updateUserPreferences: (state, action: PayloadAction<Partial<User['preferences']>>) => {
      if (state.user) {
        state.user.preferences = { ...state.user.preferences, ...action.payload }
      }
    },

    updateUserProfile: (state, action: PayloadAction<{ picture?: string; faculty?: string }>) => {
      if (state.user) {
        if (action.payload.picture !== undefined) {
          state.user.picture = action.payload.picture
        }
        if (action.payload.faculty !== undefined) {
          state.user.faculty = action.payload.faculty
        }
        
        // Persist updated auth state to localStorage
        try {
          localStorage.setItem('auth_state', JSON.stringify({
            user: state.user,
            tokens: state.tokens,
            isAuthenticated: state.isAuthenticated,
            lastLoginTime: state.lastLoginTime
          }))
        } catch (error) {
          console.warn('Failed to persist updated auth state to localStorage:', error)
        }
      }
    },
    updateUserRole: (state, action: PayloadAction<{ userId: string; role: User['role']; permissions?: string[] }>) => {
      // Only admins can update roles - this will be enforced in the thunk
      if (state.user && state.user.id === action.payload.userId) {
        state.user.role = action.payload.role
        if (action.payload.permissions) {
          state.user.permissions = action.payload.permissions
        }
        
        // Persist updated auth state to localStorage
        try {
          localStorage.setItem('auth_state', JSON.stringify({
            user: state.user,
            tokens: state.tokens,
            isAuthenticated: state.isAuthenticated,
            lastLoginTime: state.lastLoginTime
          }))
        } catch (error) {
          console.warn('Failed to persist updated auth state to localStorage:', error)
        }
      }
    },
    clearPendingVerification: (state) => {
      state.pendingVerification = null
    },
    setPendingVerification: (state, action: PayloadAction<{ username: string; email: string }>) => {
      state.pendingVerification = action.payload
    },
    // Simple login for demo purposes without Cognito
    login: (state, action: PayloadAction<Omit<User, 'preferences'> & { preferences?: Partial<User['preferences']> }>) => {
      state.user = {
        ...action.payload,
        preferences: {
          theme: 'system',
          language: 'en',
          notifications: true,
          ...action.payload.preferences
        }
      }
      state.isAuthenticated = true
      state.lastLoginTime = new Date().toISOString()
      state.error = null
      
      // Persist auth state to localStorage
      try {
        localStorage.setItem('auth_state', JSON.stringify({
          user: state.user,
          tokens: state.tokens,
          isAuthenticated: state.isAuthenticated,
          lastLoginTime: state.lastLoginTime
        }))
      } catch (error) {
        console.warn('Failed to persist auth state to localStorage:', error)
      }
    },
    // Local logout action for immediate state clearing
    logoutLocal: (state) => {
      state.user = null
      state.tokens = null
      state.isAuthenticated = false
      state.error = null
      state.loginAttempts = 0
      state.lastLoginTime = null
      state.pendingVerification = null
      
      // Clear auth state from localStorage
      try {
        localStorage.removeItem('auth_state')
      } catch (error) {
        console.warn('Failed to clear auth state from localStorage:', error)
      }
    }
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.tokens = action.payload.tokens
        state.isAuthenticated = true
        state.loginAttempts = 0
        state.lastLoginTime = new Date().toISOString()
        state.pendingVerification = null
        
        // Persist auth state to localStorage
        try {
          localStorage.setItem('auth_state', JSON.stringify({
            user: state.user,
            tokens: state.tokens,
            isAuthenticated: state.isAuthenticated,
            lastLoginTime: state.lastLoginTime
          }))
        } catch (error) {
          console.warn('Failed to persist auth state to localStorage:', error)
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.loginAttempts += 1
      })

    // Sign up
    builder
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.pendingVerification = {
          username: action.payload.username,
          email: action.payload.email
        }
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Confirm sign up
    builder
      .addCase(confirmSignUp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(confirmSignUp.fulfilled, (state) => {
        state.isLoading = false
        state.pendingVerification = null
      })
      .addCase(confirmSignUp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Resend confirmation
    builder
      .addCase(resendConfirmationCode.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(resendConfirmationCode.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(resendConfirmationCode.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Forgot password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Reset password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Refresh token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.tokens = action.payload
      })
      .addCase(refreshToken.rejected, (state) => {
        // Clear auth state if token refresh fails
        state.user = null
        state.tokens = null
        state.isAuthenticated = false
      })

    // Check auth status
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        console.log('🔄 AUTH SLICE: checkAuthStatus.pending - Starting auth check')
        state.isLoading = true
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        console.log('✅ AUTH SLICE: checkAuthStatus.fulfilled - Auth check success')
        console.log('🔍 AUTH SLICE: action.payload:', {
          payload: action.payload,
          payloadType: typeof action.payload,
          hasUser: !!action.payload?.user,
          user: action.payload?.user,
          userType: typeof action.payload?.user,
          userName: action.payload?.user?.name,
          userFirstName: action.payload?.user?.firstName,
          userLastName: action.payload?.user?.lastName,
          userEmail: action.payload?.user?.email,
          hasTokens: !!action.payload?.tokens,
          timestamp: new Date().toISOString()
        })
        
        state.isLoading = false
        if (action.payload) {
          console.log('🔥 AUTH SLICE: Setting user and tokens in Redux state')
          state.user = action.payload.user
          state.tokens = action.payload.tokens
          state.isAuthenticated = true
          
          // ULTRA DEBUG: Log final Redux state after update
          console.log('🎯 AUTH SLICE: Final Redux state after update:', {
            isAuthenticated: state.isAuthenticated,
            user: state.user,
            userType: typeof state.user,
            userName: state.user?.name,
            userFirstName: state.user?.firstName,
            userLastName: state.user?.lastName,
            userEmail: state.user?.email,
            hasTokens: !!state.tokens,
            timestamp: new Date().toISOString()
          })
        } else {
          console.log('❌ AUTH SLICE: No payload in checkAuthStatus.fulfilled')
        }
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        console.log('❌ AUTH SLICE: checkAuthStatus.rejected - Auth check failed')
        console.log('🔍 AUTH SLICE: Error payload:', action.payload)
        state.isLoading = false
        state.user = null
        state.tokens = null
        state.isAuthenticated = false
      })

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.tokens = null
        state.isAuthenticated = false
        state.error = null
        state.loginAttempts = 0
        state.lastLoginTime = null
        state.pendingVerification = null
        
        // Clear auth state from localStorage
        try {
          localStorage.removeItem('auth_state')
        } catch (error) {
          console.warn('Failed to clear auth state from localStorage:', error)
        }
      })
  }
})

export const {
  clearError,
  incrementLoginAttempts,
  resetLoginAttempts,
  updateUserPreferences,
  updateUserProfile,
  updateUserRole,
  clearPendingVerification,
  setPendingVerification,
  login,
  logoutLocal
} = authSlice.actions

export default authSlice.reducer